<template>
  <div class="widget w-earnings-calendar-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      hideLinkModeReceive
      fieldsConfigManager="advanced"
      optimizeColumns
      printView
      watchlistManager
      :wid="wid"
      @optimize-columns="autoSizeColumns"
    >
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <div class="widget-body">
      <section>
        <c-widget-control-bar>
          <v-form-group>
            <v-form-tags
              add-only-from-autocomplete
              class="country-selector"
              multicolor
              :maxTags="6"
              :autocompleteItems="filteredCountryList"
              :placeholder="i18n('labels.addCountry')"
              :value="inputCountries"
              @inputText="countryInput = $event"
              @input="onCountriesSelected"
              detached
              sameWidth
            >
              <template #tag-left="props">
                <v-flag :countryCode="props.tag.value" />
              </template>
            </v-form-tags>
          </v-form-group>
          <c-widget-control-bar-group inline>
            <v-form-group>
              <v-form-select
                :value="controlWatchlist"
                :options="optionsWatchlists"
                @input="onWatchlistSelected"
                :aria-label="i18n('labels.watchlist')"
              >
                <template slot="first">
                  <option :value="null">{{ i18n('labels.noWatchlist') }}</option>
                </template>
              </v-form-select>
            </v-form-group>
            <v-form-group>
              <v-form-select
                :value="controlCalendarView"
                :options="calendarView"
                @input="onCalendarViewSelected"
                :aria-label="i18n('labels.calendar')"
              />
            </v-form-group>
            <v-form-group class="calendar-view-shifter">
              <v-button
                variant="outline-primary"
                @click="onShiftCalendarView(-1)"
                :aria-label="i18n('labels.previousDate')"
              >
                <i class="fas fa-chevron-left" />
              </v-button>
              <v-button
                variant="outline-primary"
                @click="onShiftCalendarView(0)"
                :class="{ selected: isTodaySelected }"
              >
                {{ i18n('labels.today') }}
              </v-button>
              <v-button variant="outline-primary" @click="onShiftCalendarView(1)" :aria-label="i18n('labels.nextDate')">
                <i class="fas fa-chevron-right" />
              </v-button>
            </v-form-group>
          </c-widget-control-bar-group>
        </c-widget-control-bar>
        <div class="widget-body">
          <div :class="['calendar', controlCalendarView]">
            <div
              v-for="(momentDate, i) in calendarDates"
              class="cell"
              :class="getDayClassname(momentDate, calendarDates, i)"
              :key="formatDate(momentDate, 'MMDD')"
              @click="onCalendarDayClick(momentDate, $event)"
            >
              <template v-if="isCalendarWeekView || momentDate.isSame(currentDateStart, 'month')">
                <div class="weekday">{{ i18n('calendar.weekdays.' + momentDate.day()) }}</div>
                <div class="date">{{ momentDate.date() }} {{ i18n('calendar.months.' + momentDate.month()) }}</div>
                <div class="counts" v-if="!isCalendarLoading && counters[formatDate(momentDate, 'YYYY-MM-DD')]">
                  <div
                    class="country"
                    v-for="country in controlCountries"
                    :key="country"
                    v-show="counters[formatDate(momentDate, 'YYYY-MM-DD')][country]"
                  >
                    <span>{{ counters[formatDate(momentDate, 'YYYY-MM-DD')][country] }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>
      <section v-if="controlCountries.length">
        <c-widget-control-bar>
          <v-form-group :label="i18n('labels.period')" label-cols="auto">
            <v-form-select
              :options="periodOptions"
              :value="selectedPeriod"
              class="period-select"
              @input="onPeriodInput"
              :aria-label="i18n('labels.period')"
            />
          </v-form-group>
          <v-form-group :label="i18n('labels.from')" label-cols="auto">
            <v-form-datepicker
              detached
              boundary="window"
              value-as-date
              :value="currentDateStart.toDate()"
              :min="minFromDate"
              :max="currentDateEnd.toDate()"
              :locale="$i18n.locale"
              :labelHelp="$t('white-label.components.datepicker.labelHelp')"
              :labelPrevMonth="$t('white-label.components.datepicker.labelPrevMonth')"
              :labelCurrentMonth="$t('white-label.components.datepicker.labelCurrentMonth')"
              :labelNextMonth="$t('white-label.components.datepicker.labelNextMonth')"
              :labelPrevYear="$t('white-label.components.datepicker.labelPrevYear')"
              :labelCurrentYear="$t('white-label.components.datepicker.labelCurrentYear')"
              :labelNextYear="$t('white-label.components.datepicker.labelNextYear')"
              @input="onDateRangeInput('Start', $event)"
            />
          </v-form-group>
          <v-form-group :label="i18n('labels.to')" label-cols="auto">
            <v-form-datepicker
              detached
              boundary="window"
              value-as-date
              :value="currentDateEnd.toDate()"
              :min="currentDateStart.toDate()"
              :max="maxToDate"
              :locale="$i18n.locale"
              @input="onDateRangeInput('End', $event)"
            />
          </v-form-group>
          <v-form-group :label="$t('white-label.globalLabels.template')" label-cols="auto">
            <v-form-select
              :options="fieldsConfigNameOptions"
              v-model="controlFieldsConfigName"
              :aria-label="$t('white-label.globalLabels.template')"
            />
          </v-form-group>
        </c-widget-control-bar>
        <div class="widget-body" :style="{ flexBasis: tableMinHeight + 'px' }">
          <v-alert show v-if="isNoData" variant="light" align="center">{{ i18n('nodata', 'globalMessages') }}</v-alert>
          <v-alert show v-if="isError" variant="red" align="center">{{ i18n('error', 'globalMessages') }}</v-alert>
          <c-loader v-if="isTableLoading" />
          <div v-show="!isNoData && !isError" class="table-container">
            <ag-grid-vue
              v-show="!isTableLoading"
              class="data-table ag-theme-balham-dark"
              animateRows
              :asyncTransactionWaitMillis="gridUpdateInterval"
              ensureDomOrder
              :cacheBlockSize="tableCacheBlockSize"
              :columnDefs="columnDefs"
              :defaultColDef="defaultColumnDef"
              :gridOptions="gridOptions"
              :rowHeight="agGridRowHeight"
              :context="this"
              rowModelType="serverSide"
              rowSelection="single"
              :tooltipShowDelay="1"
              serverSideStoreType="partial"
              suppressContextMenu
              suppressFieldDotNotation
              suppressScrollOnNewData
              suppressDragLeaveHidesColumns
              :suppressColumnVirtualisation="isAccessibilityEnabled"
              @column-moved="onColMoved"
              @drag-stopped="onGridDragEnd"
              @column-resized="onColResized"
              @grid-ready="onGridReady"
              @sort-changed="onTableSort"
              @row-selected="updateLink"
              @cell-focused="onCellFocused"
              @new-columns-loaded="ev => setColumnState(ev.columnApi)"
              @async-transactions-flushed="onAsyncTransactionsFlushed"
            />
          </div>
        </div>
      </section>
    </div>

    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';

import { API, fromSymbol } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';
import { FlagRenderer, RowButtonRenderer } from '@custom/dataGrid/renderers';
import { toTimezoneDate } from '@/utils/date';
import moment from 'moment';
import resize from 'vue-resize-directive';
import agGridCommons from '@mixins/agGridCommons.js';

export default {
  name: 'w-earnings-calendar-widget',
  directives: {
    resize
  },
  mixins: [agGridCommons],
  components: {
    AgGridVue,
    CLoader,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    CWidgetHeader,
    FlagRenderer,
    RowButtonRenderer
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      companiesPerWeekDay: 23,
      counters: {},
      countryInput: '',
      currentDateStart: this.momentTZ(),
      currentDateEnd: this.momentTZ(),
      tableFieldsPrepend: ['action'],
      gridOptions: {
        columnTypes: { actionable: {} },
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      },
      gridApi: {},
      isCalendarLoading: false,
      isError: false,
      isNoData: false,
      isTableLoading: false,
      tableCacheBlockSize: 50,
      tableResultsCount: 0,
      earningsFidsPrefixes: ['earn', 'q', 'x', 'xtrsrv']
    };
  },
  computed: {
    fullCountryList() {
      return [
        {
          text: this.$t('white-label.countries.CA'),
          value: 'CA'
        },
        {
          text: this.$t('white-label.countries.US'),
          value: 'US'
        }
      ];
    },
    controlCalendarView() {
      return this.$store.state.widget[this.wid].calendarView;
    },
    controlCountries() {
      return this.$store.state.widget[this.wid].countries;
    },
    controlWatchlist() {
      const selectedWatchlist = this.$store.state.widget[this.wid].selectedWatchlist;
      return selectedWatchlist && this.$store.getters['watchlists/getWatchlist'](selectedWatchlist)
        ? selectedWatchlist
        : null;
    },
    activeWatchlist() {
      return this.$store.getters['watchlists/getWatchlist'](this.controlWatchlist);
    },
    periodOptions() {
      return [
        { text: this.i18n('labels.today'), value: 'today' },
        { text: this.i18n('labels.next3days'), value: 'next3d' },
        { text: this.i18n('labels.nextWeek'), value: 'next1w' },
        { text: this.i18n('labels.next2Weeks'), value: 'next2w' },
        { text: this.i18n('labels.nextMonth'), value: 'next-month' },
        // { text: this.i18n('labels.1day'), value: '1day' },
        // { text: this.i18n('labels.1week'), value: '1week' },
        // { text: this.i18n('labels.1month'), value: '1month' },
        { text: this.i18n('labels.custom'), value: 'custom', disabled: this.selectedPeriod !== 'custom' }
      ];
    },
    calendarView() {
      // eslint-disable-next-line
      return [{ value: 'month', text: this.i18n('labels.month') }, { value: 'week', text: this.i18n('labels.week') }];
    },
    calendarDataUpdateHash() {
      return [
        this.controlCountries.join(),
        this.rics,
        this.indexRics,
        this.controlCalendarView,
        this.formatDate(this.currentDateStart, 'Y' + (this.isCalendarWeekView ? 'W' : 'M'))
      ].join(';');
    },
    calendarDates() {
      const dateStart = this.currentDateStart.clone();
      const dateEnd = this.currentDateStart.clone();
      if (this.isCalendarWeekView) {
        dateStart.startOf('isoWeek');
        dateEnd.endOf('isoWeek');
      } else {
        dateStart.startOf('month').startOf('isoWeek');
        dateEnd.endOf('month').endOf('isoWeek');
      }
      const currMoment = dateStart.clone();
      const days = [currMoment.clone()];
      do {
        currMoment.add(1, 'day');
        days.push(currMoment.clone());
      } while (!currMoment.isSame(dateEnd, 'day'));
      return days;
    },
    calendarSelectedDate() {
      return this.formatDate(this.currentDateStart, 'YYYY-MM-DD');
    },
    filteredCountryList() {
      const countryList = this.fullCountryList.filter(entity => {
        return entity.text.toLowerCase().includes(this.countryInput.toLowerCase());
      });
      return countryList;
    },
    inputCountries() {
      return this.controlCountries.map(code => ({
        text: this.fullCountryList.find(v => v.value === code).text,
        value: code
      }));
    },
    isCalendarWeekView() {
      return this.controlCalendarView === 'week';
    },
    isTodaySelected() {
      return (
        this.momentTZ().isSame(this.currentDateStart, 'day') && this.currentDateStart.isSame(this.currentDateEnd, 'day')
      );
    },
    rics() {
      return this.controlWatchlist ? this.activeWatchlist.items.filter(ric => ric.indexOf('0#') === -1).join() : '';
    },
    indexRics() {
      return this.controlWatchlist
        ? this.activeWatchlist.items
            .filter(ric => ric.indexOf('0#') === 0)
            .map(ric => ric.replace('0#', ''))
            .join()
        : '';
    },
    optionsWatchlists() {
      return this.$store.getters['watchlists/watchlistsSorted'].map(watchlist => {
        return {
          text: watchlist.name,
          value: watchlist.id,
          disabled: !watchlist.items || !watchlist.items.length
        };
      });
    },
    selectedPeriod() {
      if (this.isTodaySelected) return 'today';
      if (this.currentDateStart.isSame(this.currentDateEnd, 'day')) return '1day';
      if (
        this.currentDateStart.isoWeekday() === 1 &&
        this.currentDateEnd.isSame(this.currentDateStart.clone().endOf('isoWeek'), 'day')
      )
        return '1week';
      if (
        this.currentDateStart.get('date') === 1 &&
        this.currentDateStart.isSame(this.currentDateEnd, 'month') &&
        this.currentDateEnd.isSame(this.currentDateEnd.clone().endOf('month'), 'day')
      )
        return '1month';
      if (this.currentDateStart.isSame(this.momentTZ(), 'day')) {
        if (
          this.momentTZ()
            .add(3, 'days')
            .isSame(this.currentDateEnd, 'day')
        )
          return 'next3d';
        if (
          this.momentTZ()
            .add(1, 'week')
            .isSame(this.currentDateEnd, 'day')
        )
          return 'next1w';
        if (
          this.momentTZ()
            .add(2, 'weeks')
            .isSame(this.currentDateEnd, 'day')
        )
          return 'next2w';
        if (
          this.momentTZ()
            .add(1, 'month')
            .isSame(this.currentDateEnd, 'day')
        )
          return 'next-month';
      }
      return 'custom';
    },
    extendedColDefs() {
      return this._.merge(
        {
          action: {
            cellRenderer: 'RowButtonRenderer',
            width: 40
          },
          'earn.ACTUAL': {
            cellClass: this.calcTableActualCellClass
          }
        },
        //handle sortable columns
        this.tableFids.reduce((acc, fid) => {
          if (!fid.startsWith('earn.')) {
            acc[fid] = { sortable: false };
          }
          return acc;
        }, {})
      );
    },
    earningsFids() {
      return this.requestFids.filter(rfid => {
        //remove unsupported fids for earnings
        return this.earningsFidsPrefixes.some(prefix => rfid.startsWith(`${prefix}.`));
      });
    },
    tableRequestFids() {
      return this.requestFids.filter(rfid => !rfid.startsWith('earn.'));
    },
    tableMinHeight() {
      return this.tableResultsCount
        ? Math.min(this.agGridRowHeight * (this.tableResultsCount + 1), this.agGridRowHeight * 16) + 7
        : 70;
    },
    tableDataUpdateHash() {
      return [
        this.controlCountries.join(),
        this.rics,
        this.indexRics,
        this.formatDate(this.currentDateStart, 'YMMDD'),
        this.formatDate(this.currentDateEnd, 'YMMDD'),
        this.tableSort.map(r => `${r.colId}:${r.sort}`).join()
      ].join(';');
    },
    minFromDate() {
      const currentFromDate = new Date(this.currentDateEnd);
      return new Date(currentFromDate.setDate(currentFromDate.getDate() - 30));
    },
    maxToDate() {
      const currentToDate = new Date(this.currentDateStart);
      return new Date(currentToDate.setDate(currentToDate.getDate() + 30));
    },
    tableSort() {
      return this.widgetStore.tableSort;
    }
  },
  watch: {
    '$store.state.applicationSettings.general.settings.timezone'(newTZ, oldTZ) {
      // if today was selected before TZ change, reselect it in case it's not anymore
      if (
        this.currentDateStart.isSame(this.currentDateEnd, 'day') &&
        this.currentDateStart.isSame(toTimezoneDate(oldTZ), 'day') &&
        toTimezoneDate(oldTZ).getDate() !== toTimezoneDate(newTZ).getDate()
      )
        this.onPeriodInput('today');
    },
    calendarDataUpdateHash() {
      this.fetchCalendarData();
    },
    // temporary addon for fontSize change handler in agGridCommons, gridApi.resetRowHeights() seems to not work with serverSide row model
    fontSize() {
      this.gridApi.forEachNode(node => node.setRowHeight(this.agGridRowHeight));
      this.gridApi.onRowHeightChanged();
    },
    tableDataUpdateHash() {
      this.isTableLoading = true;
      // there's no way to properly clear the rows from the table without ag-grid full rerender, so let's reset ServerSideDatasource
      this.fetch();
    }
  },
  methods: {
    calcTableActualCellClass(params) {
      if (isNaN(params.value) || isNaN(params.data['earn.EST_MEAN']) || params.value === params.data['earn.EST_MEAN'])
        return '';
      return params.value > params.data['earn.EST_MEAN'] ? 'positive' : 'negative';
    },
    // Load Data
    // TODO: debounce load requests
    fetchCalendarData() {
      this.isCalendarLoading = true;
      API.Rest.get(
        `/company/earnings?fids=x._COUNTRY,earn.COMBI_DATE,${
          this.isCalendarWeekView ? 'x._TICKER' : 'xtrsrv._FINAL_DISTINCT_COUNT'
        }&countries=${this.controlCountries}${this.rics ? '&rics=' + this.rics : ''}${
          this.indexRics ? '&indexRics=' + this.indexRics : ''
        }&fromDate=${this.formatDate(
          this.currentDateStart.clone().startOf(this.isCalendarWeekView ? 'isoWeek' : 'month'),
          'YYYY-MM-DD'
        )}&toDate=${this.formatDate(
          this.currentDateStart
            .clone()
            .endOf(this.isCalendarWeekView ? 'isoWeek' : 'month')
            .add(1, 'day'),
          'YYYY-MM-DD'
        )}&pageSize=9999${this.isCalendarWeekView ? '' : '&groupBy=x._COUNTRY,earn.COMBI_DATE'}`
      ).then(response => {
        const data = response.data.data;
        // calculate the amount of tickers per country which will fit into weekday cell
        if (this.isCalendarWeekView) {
          this.counters = data.reduce((acc, entity) => {
            const date = entity['earn.COMBI_DATE'].substr(0, 10);
            const country = entity['x._COUNTRY'];
            if (!acc[date]) acc[date] = {};
            if (!acc[date][country]) acc[date][country] = [];
            acc[date][country].push(entity['x._TICKER']);
            return acc;
          }, {});
          const tickersPerCountry = Math.round(this.companiesPerWeekDay / this.controlCountries.length);
          Object.values(this.counters).forEach(countries => {
            let localTickersPerCountry = tickersPerCountry;
            const countriesCount = Object.keys(countries).length;
            Object.values(countries).forEach(tickers => {
              const tickerCount = tickers.length;
              if (tickersPerCountry > tickerCount) {
                localTickersPerCountry += Math.round((tickersPerCountry - tickerCount) / (countriesCount - 1));
              }
            });
            Object.keys(countries).forEach(country => {
              const tickers = countries[country];
              const tickersCount = tickers.length;
              if (tickersCount > localTickersPerCountry)
                tickers.splice(
                  localTickersPerCountry - 1,
                  9999999,
                  tickersCount - localTickersPerCountry + 1 + ' more'
                );
              countries[country] = tickers.join('\n');
            });
          });
        } else {
          this.counters = data.reduce((acc, entity) => {
            const date = entity['earn.COMBI_DATE'].substr(0, 10);
            if (!acc[date]) acc[date] = {};
            if (!acc[date][entity['x._COUNTRY']]) acc[date][entity['x._COUNTRY']] = 0;
            acc[date][entity['x._COUNTRY']] += +entity['xtrsrv._FINAL_DISTINCT_COUNT'];
            return acc;
          }, {});
        }
        this.isCalendarLoading = false;
      });
    },
    fetch() {
      //FIXME: unsubscribing only when fetching fresh data. Can optimize further to subscribe only the new rows and unsubscribe any rows not visible.
      this.streamingUnsubscribe();
      this.gridApi.setServerSideDatasource({
        getRows: this.fetchTableData
      });
    },
    fetchTableData(params) {
      if (!this.controlCountries.length) return;
      if (params.request.startRow === 0) {
        this.isTableLoading = true;
        this.tableResultsCount = 0;
      }
      this.isError = this.isNoData = false;

      const sortFid = params.request.sortModel.length
        ? params.request.sortModel[0].colId.replace(/[^.]+\./, '')
        : 'COMBI_DATE';
      const sortType = ['EST_MEAN', 'EST_NUM', 'PREV_YEAR_ACTUAL', 'PREV_QUARTER_ACTUAL', 'ACTUAL'].includes(sortFid)
        ? 'N'
        : 'A';
      API.Rest.get(
        `/company/earnings?fids=q.RIC,${this.requestFids},earn.EST_CURRENCY&countries=${this.controlCountries}${
          this.rics ? '&rics=' + this.rics : ''
        }${this.indexRics ? '&indexRics=' + this.indexRics : ''}&fromDate=${this.formatDate(
          this.currentDateStart,
          'YYYY-MM-DD'
        )}&toDate=${this.formatDate(
          this.currentDateEnd.clone().add(1, 'day'),
          'YYYY-MM-DD'
        )}&sortFids=${sortFid}&sortDirs=${
          params.request.sortModel.length ? params.request.sortModel[0].sort[0].toUpperCase() : 'A'
        }&sortTypes=${sortType}&pageSize=${params.request.endRow - params.request.startRow}&pageNo=${Math.floor(
          params.request.startRow / (params.request.endRow - params.request.startRow)
        )}`
      ).then(response => {
        const snapshot = response.data.data;
        if (params.request.startRow === 0) {
          this.tableResultsCount = snapshot.length;
        } else {
          this.tableResultsCount += snapshot.length;
        }

        if (!this.formats) {
          this.formats = Object.keys(response.data.metadata).reduce((acc, fid) => {
            acc[fid] = response.data.metadata[fid].type;
            return acc;
          }, {});
          this.formats['earn.COMBI_DATE'] = 'date.localed';
        }
        if (snapshot.length) {
          snapshot.forEach(rowData => {
            Object.keys(rowData).forEach(fid => {
              rowData[fid] = rowData[fid];
              switch (fid) {
                case 'earn.COMBI_DATE':
                  rowData[fid] = this.$f(rowData[fid], 'date.localed');
                  break;
                case 'earn.MARKET_PHASE':
                  switch (rowData[fid]) {
                    case '1':
                      rowData[fid] = this.i18n('table.beforeMarketOpen');
                      break;
                    case '2':
                      rowData[fid] = this.i18n('table.duringMarketOpen');
                      break;
                    case '3':
                      rowData[fid] = this.i18n('table.afterMarketClose');
                      break;
                  }
                  break;
                case 'earn.PREV_QUARTER_ACTUAL':
                case 'earn.PREV_YEAR_ACTUAL':
                case 'earn.EST_MEAN':
                case 'earn.ACTUAL':
                  switch (rowData['earn.EST_CURRENCY']) {
                    case 'USD':
                      rowData[fid] += ' $';
                      break;
                    case 'CAD':
                      rowData[fid] += ' C$';
                      break;
                    default:
                      rowData[fid] += ` ${rowData['earn.EST_CURRENCY']}`;
                      break;
                  }
              }
            });
          });

          const tickers = snapshot.map(rowData => `${rowData['q._TCR']}:${rowData['q._COUNTRY']}`);
          fromSymbol(tickers)
            .then(rics => {
              if (!rics.length) {
                console.warn('No valid RICS for quote subscription');
                return;
              }
              return this.getQuoteSnapshot(rics).then(quoteSnapshot => {
                quoteSnapshot.forEach(quoteData => {
                  snapshot.forEach(rowData => {
                    if (rowData['q._TCR'] === quoteData['q._TCR']) {
                      Object.assign(rowData, this.hydrateData(quoteData, rowData));
                    }
                  });
                });

                this.streamingSubscribe(rics);
              });
            })
            .finally(() => {
              //display earnings data even if quote data not available
              params.success({
                rowData: snapshot,
                ...(snapshot.length < this.tableCacheBlockSize && { rowCount: this.tableResultsCount })
              });
            });
        } else {
          params.fail();
          this.isNoData = true;
        }
        this.isTableLoading = false;
      });
    },
    formatDate(momentDate, format) {
      return momentDate.format(format);
    },
    getDayClassname(momentDate, momentDates, i) {
      const classNames = [];
      const isStartOfWeek = momentDate.isoWeekday() === 1;
      if (isStartOfWeek) classNames.push('week-start');
      if (this.isDateSelected(momentDate)) {
        classNames.push('selected');
        if (i > 0 && this.isDateSelected(momentDates[i - 1])) {
          if (!isStartOfWeek) classNames.push('no-left');
          if (i - 7 >= 0 && this.isDateSelected(momentDates[i - 7])) classNames.push('no-top');
        }
        if (i < momentDates.length - 1 && this.isDateSelected(momentDates[i + 1])) {
          if (momentDate.isoWeekday() < 7) classNames.push('no-right');
          if (i + 7 < momentDates.length && this.isDateSelected(momentDates[i + 7])) classNames.push('no-bottom');
        }
      }
      return classNames;
    },
    getQuoteSnapshot(rics) {
      return API.Rest.get(`/quote/info?rics=${rics}&fids=${this.tableRequestFids}`).then(response => {
        Object.assign(
          this.formats,
          Object.keys(response.data.metadata).reduce((acc, fid) => {
            acc[fid] = response.data.metadata[fid].type;
            return acc;
          }, {})
        );

        return response.data.data;
      });
    },
    handleSubscription(_response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming subscription error', error);
      } else {
        this.hasStreamingError = false;
      }
    },
    handleSnapshot(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming snapshot error', error);
        return;
      }
      this.hasStreamingError = false;

      this.handleUpdate(response, error);
    },
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming update error', error);
        return;
      }
      this.hasStreamingError = false;

      const { ric, data } = response.payload[0];

      if (this.gridApi) {
        this.gridApi.forEachNode(node => {
          if (node.data && node.data['q.RIC'] === ric) {
            node.data = this.hydrateData(data, node.data);
            this.gridApi.applyServerSideTransaction({ update: node.data });
            this.gridApi.refreshCells({ rowNodes: [node] });
          }
        });
      }
    },
    isDateSelected(momentDate) {
      return momentDate.isBetween(this.currentDateStart, this.currentDateEnd, 'day', '[]');
    },
    momentTZ(date) {
      return moment(toTimezoneDate(this.$store.state.applicationSettings.general.settings.timezone, date));
    },
    onCalendarDayClick(momentDate, ev) {
      if (ev.shiftKey) {
        if (momentDate.isSameOrAfter(this.currentDateStart, 'day')) this.currentDateEnd = momentDate;
        else this.currentDateStart = momentDate;
      } else {
        this.currentDateStart = momentDate;
        this.currentDateEnd = momentDate;
      }
    },
    onCalendarViewSelected(val) {
      this.$store.commit(`widget/${this.wid}/CALENDAR_VIEW_CHANGED`, val);
    },
    onCountriesSelected(val) {
      val = val.map(entity => entity.value);
      this.$store.commit(`widget/${this.wid}/COUNTRIES_CHANGED`, val);
    },
    onDateRangeInput(side, val) {
      this['currentDate' + side] = moment(val);
      if (this.currentDateEnd.isBefore(this.currentDateStart, 'day')) {
        this.$notify.error(this.i18n('messages.invalidDateRange.title'), this.i18n('messages.invalidDateRange.text'), {
          group: this.notifierId,
          closeButton: true
        });
      }
    },
    onGridReady(ev) {
      this.gridApi = ev.api;
      this.fetch();
      this.onTableReady(ev, this.i18n('widget-title'));
    },
    onPeriodInput(period) {
      switch (period) {
        case '1day':
          this.currentDateEnd = this.currentDateStart.clone();
          break;
        case 'today':
          this.currentDateStart = this.momentTZ();
          this.currentDateEnd = this.momentTZ();
          break;
        case 'next3d':
          this.currentDateStart = this.momentTZ();
          this.currentDateEnd = this.momentTZ().add(3, 'days');
          break;
        case 'next1w':
          this.currentDateStart = this.momentTZ();
          this.currentDateEnd = this.momentTZ().add(1, 'week');
          break;
        case 'next2w':
          this.currentDateStart = this.momentTZ();
          this.currentDateEnd = this.momentTZ().add(2, 'weeks');
          break;
        case 'next-month':
          this.currentDateStart = this.momentTZ();
          this.currentDateEnd = this.momentTZ().add(1, 'month');
          break;
        case '1week':
          this.currentDateStart = this.currentDateStart.clone().startOf('isoWeek');
          this.currentDateEnd = this.currentDateStart.clone().endOf('isoWeek');
          break;
        case '1month':
          this.currentDateStart = this.currentDateStart.clone().startOf('month');
          this.currentDateEnd = this.currentDateStart.clone().endOf('month');
          break;
      }
    },
    onShiftCalendarView(dir) {
      const isoPeriod = this.isCalendarWeekView ? 'isoWeek' : 'month';
      switch (dir) {
        case -1:
          this.currentDateStart = this.currentDateStart
            .clone()
            .subtract(1, this.controlCalendarView)
            .startOf(isoPeriod);
          this.currentDateEnd = this.currentDateStart.clone().endOf(isoPeriod);
          break;
        case 0:
          this.currentDateStart = this.momentTZ();
          this.currentDateEnd = this.momentTZ();
          break;
        case 1:
          this.currentDateStart = this.currentDateStart
            .clone()
            .add(1, this.controlCalendarView)
            .startOf(isoPeriod);
          this.currentDateEnd = this.currentDateStart.clone().endOf(isoPeriod);
          break;
      }
    },
    onWatchlistSelected(val) {
      this.$store.commit(`widget/${this.wid}/SELECTED_WATCHLIST_CHANGED`, val);
    }
  },
  created() {
    this.fetchCalendarData();
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>

<style lang="scss">
@import '~@/styles/vendor/ag-grid';
@import '~@/styles/widgets/earnings-calendar-widget';
</style>
